import { css } from 'styled-components'

import { BuildPassTheme } from '../../types/BuildPassTheme'

const mediaWrapper =
  (size: number) =>
  (...args: [TemplateStringsArray]) => css`
    @media (min-width: ${size / 16}rem) {
      ${css(...args)}
    }
  `

export const buildPassTheme: BuildPassTheme = {
  sideNav: {
    fullWidth: 256,
    collapsedWidth: 80,
  },
  borderRadius: {
    small: '4px', // Round - default - typically use for buttons, inputs, spans, icons, small cards
    medium: '6px', // Rounder - used for most cards, wrappers, some larger/primary buttons
    large: '8px', // Rounderer - used to accent larger cards, wrappers, modals
    max: '999px', // Roundest - used for round edges
  },
  breakpoints: {
    xxl: 1600,
    xl: 1280,
    lg: 1024,
    md: 768,
    sm: 576,
  },
  media: {
    xxl: mediaWrapper(1600),
    xl: mediaWrapper(1280),
    lg: mediaWrapper(1024),
    md: mediaWrapper(768),
    sm: mediaWrapper(576),
  },
  modal: {
    xsmall: 300, // Use for single button confirm modals without titles + very limited text, or alerts
    small: 600, // Use for basic cancel/confirm modals with multiple buttons + few sentences of text
    medium: 800, // Use for forms
    large: 1000, // Use for large displays such as tables or complex forms
  },
  colour: {
    ai: {
      light: '#DCC8F9',
      dark: '#6E0AFF',
      animationColourA: '#6DDCFF',
      animationColourB: '#C7A3FC',
    },
    theme: {
      primaryColour: null, // filled in by themeContext.tsx
      buttonTextColour: null, // filled in by themeContext.tsx
      primaryTextColour: null, // filled in by themeContext.tsx
    },
    primary: {
      base: '#85CA0C',
      _900: '#3F6006',
      _800: '#527D07',
      _700: '#5F9108',
      _600: '#72AE0A',
      _500: '#85CA0C',
      _400: '#A2F218',
      _300: '#C9F778',
      _200: '#D6F7A9',
      _100: '#E2F6DA',
      _50: '#EFFAEA',
    },
    secondary: {
      base: '#171717',
      _900: '#171717',
      _800: '#262626',
      _700: '#262626',
      _600: '#525252',
      _500: '#737373',
      _400: '#A3A3A3',
      _300: '#D4D4D4',
      _200: '#E5E5E5',
      _100: '#F5F5F5',
      _50: '#F9F9F9',
    },
    link: {
      base: '#5F9108',
    },
    semantic: {
      success: '#72AE0A',
      successDark: '#527D07',
      successLight: '#E2F6DA',
      warning: '#FBBF24',
      warningDark: '#92400E',
      warningLight: '#FEF3C7',
      error: '#E38C8C',
      errorDark: '#AC2A2A',
      errorLight: '#F5D6D6',
    },
    neutral: {
      darkText: '#171717',
      mediumText: '#525252',
      lightText: '#A3A3A3',
      border: '#E5E5E5',
      surface: '#F5F5F5',
      background: '#F9F9F9',
      white: '#ffffff',
    },
    other: {
      gold: '#f7b92a',
      hazel: '#a68362',
      brown: '#705035',
      orange: '#f9813d',
      yellow: '#FCD34D',
      lime: '#A2F218',
      pine: '#3F6006',
      cyan: '#6DDCFF',
      blue: '#175CD3',
      purple: '#6E0AFF',
      pink: '#ff5bbe',
      red: '#CF3A3A',
      darkRed: '#d06d68',
    },
  },
  font: {
    display01: css`
      font-family: 'geist';
      font-weight: 500;
      font-size: 3rem;
      line-height: 3.75rem;
      letter-spacing: -2%;
    `,
    display02: css`
      font-family: 'geist';
      font-weight: 500;
      font-size: 2.25rem;
      line-height: 2.75rem;
      letter-spacing: -2%;
    `,
    heading01: css`
      font-family: 'geist';
      font-weight: 600;
      font-size: 1.875rem;
      line-height: 2.375rem;
    `,
    heading02: css`
      font-family: 'geist';
      font-weight: 600;
      font-size: 1.5rem;
      line-height: 2rem;
    `,
    heading03: css`
      font-family: 'geist';
      font-weight: 600;
      font-size: 1.25rem;
      line-height: 1.875rem;
    `,
    heading04: css`
      font-family: 'geist';
      font-weight: 600;
      font-size: 1.125rem;
      line-height: 1.75rem;
    `,
    label01: css`
      font-family: 'geist';
      font-weight: 500;
      font-size: 1rem;
      line-height: 1.5rem;
    `,
    label02: css`
      font-family: 'geist';
      font-weight: 500;
      font-size: 0.875rem;
      line-height: 1.25rem;
    `,
    subheading01: css`
      font-family: 'geist';
      font-weight: 600;
      font-size: 1rem;
      line-height: 1.5rem;
    `,
    subheading02: css`
      font-family: 'geist';
      font-weight: 500;
      font-size: 0.875rem;
      line-height: 1.25rem;
    `,
    body01: css`
      font-family: 'geist';
      font-weight: 400;
      font-size: 1rem;
      line-height: 1.5rem;
    `,
    body02: css`
      font-family: 'geist';
      font-weight: 400;
      font-size: 1rem;
      line-height: 1.5rem;
    `,
    body03: css`
      font-family: 'geist';
      font-weight: 400;
      font-size: 0.875rem;
      line-height: 1.25rem;
    `,
    caption01: css`
      font-family: 'geist';
      font-weight: 400;
      font-size: 0.75rem;
      line-height: 1.125rem;
    `,
    caption02: css`
      font-family: 'geist';
      font-weight: 400;
      font-size: 0.75rem;
      line-height: 1.125rem;
    `,
    footer: css`
      font-family: 'geist';
      font-weight: 600;
      font-size: 0.75rem;
      line-height: 1.125rem;
    `,
  },
}
